import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Code2, Globe, Mail, Zap, ShieldCheck, Clock, Play, ArrowRight,
  Brain, SlidersHorizontal, Smartphone, MousePointer2, Database,
  ListChecks, Send, Star, ChevronRight,
  Bot, Activity, Sparkles, Lock,
} from "lucide-react";
import { DemoVideoModal } from "../components/DemoVideoModal";

const BG = "#05060e";

/* Scroll-reveal wrapper — fades + levitates content into view once. */
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(34px)",
        transition: "opacity 0.7s ease-out, transform 0.75s cubic-bezier(0.22,1,0.36,1)",
        transitionDelay: `${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

const LOGOS = ["NEXNOVA", "ORBIT", "LUMEN", "VANTAGE", "PULSE", "CORTEX", "AETHER", "NOVA"];
const TESTIMONIALS = [
  {
    name: "Camille Laurent",
    role: "Product Manager · Nexnova",
    quote: "Sara gère mes emails et mes recherches pendant que je me concentre sur la stratégie. J'ai gagné 2 heures par jour, sans exagérer.",
    initials: "CL",
  },
  {
    name: "Marc Dubois",
    role: "Développeur freelance",
    quote: "La génération de code est bluffante : elle sort des composants propres que je n'ai plus qu'à intégrer. Un vrai copilote.",
    initials: "MD",
  },
  {
    name: "Inès Moreau",
    role: "Fondatrice · Studio Lumen",
    quote: "L'automatisation web m'a fait économiser des semaines. Sara s'occupe du répétitif, moi du créatif. Indispensable.",
    initials: "IM",
  },
];

/* ---- small building blocks ------------------------------------------ */
function GradientText({ children }) {
  return (
    <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-violet-400 bg-clip-text text-transparent">
      {children}
    </span>
  );
}

function PrimaryBtn({ to = "/auth", children, className = "", ...props }) {
  return (
    <Link
      to={to}
      className={`group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white
        bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-400 hover:to-violet-400
        shadow-[0_10px_40px_-10px_rgba(99,102,241,0.7)] transition-all duration-300 hover:shadow-[0_14px_50px_-8px_rgba(139,92,246,0.75)] hover:-translate-y-0.5 ${className}`}
      {...props}
    >
      {children}
    </Link>
  );
}

function GhostBtn({ href = "#", children, ...props }) {
  return (
    <a
      href={href}
      className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium text-slate-200
        border border-white/12 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/25 transition-all duration-300"
      {...props}
    >
      {children}
    </a>
  );
}

function DemoBtn({ onClick, ...props }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium text-slate-200
        border border-white/12 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/25 transition-all duration-300"
      {...props}
    >
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400/20 text-cyan-300 transition-transform group-hover:scale-110">
        <Play className="h-3 w-3 translate-x-[1px]" />
      </span>
      Découvrir Sara
    </button>
  );
}

function HeroBullet({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-gradient-to-br from-blue-500/15 to-violet-500/15">
        <Icon className="h-4 w-4 text-cyan-300" />
      </div>
      <div>
        <div className="text-sm font-semibold text-white">{title}</div>
        <div className="text-xs text-slate-400 leading-snug">{sub}</div>
      </div>
    </div>
  );
}

function GlassCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-xl p-6 sm:p-7
        shadow-[0_20px_60px_-25px_rgba(0,0,0,0.9)] transition-all duration-300 hover:border-white/20 hover:-translate-y-1
        hover:shadow-[0_28px_70px_-25px_rgba(59,130,246,0.35)] ${className}`}
    >
      {children}
    </div>
  );
}

function UnderstandItem({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-blue-500/20 to-violet-500/20">
        <Icon className="h-5 w-5 text-cyan-300" />
      </div>
      <div>
        <div className="text-base font-semibold text-white">{title}</div>
        <p className="mt-1 text-sm text-slate-400 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

/* ---- feature mini mockups ------------------------------------------- */
function CodeMock() {
  const langs = ["Python", "JavaScript", "TypeScript", "React", "Node.js", "PHP"];
  return (
    <div className="mt-6 rounded-xl border border-white/10 bg-[#0a0e1a]/80 overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-white/5 px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-blue-400/70" />
        <span className="font-mono text-[10px] text-slate-500 ml-1">Python</span>
        <span className="ml-auto flex gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />
          <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />
          <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />
        </span>
      </div>
      <div className="flex min-w-0">
        <div className="w-28 shrink-0 border-r border-white/5 p-2 space-y-1">
          {langs.map((l, i) => (
            <div
              key={l}
              className={`flex items-center gap-1.5 rounded px-1.5 py-1 text-[11px] ${
                i === 0 ? "bg-blue-500/15 text-blue-200" : "text-slate-500"
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
              {l}
            </div>
          ))}
        </div>
        <pre className="flex-1 min-w-0 overflow-x-auto p-3 font-mono text-[11px] leading-5 text-slate-300">
{`def `}<span className="text-cyan-300">creer_application</span>{`(idee):
    `}<span className="text-violet-400">return</span>{` {
        `}<span className="text-emerald-300">"type"</span>{`: `}<span className="text-amber-300">"web"</span>{`,
        `}<span className="text-emerald-300">"framework"</span>{`: `}<span className="text-amber-300">"nextjs"</span>{`,
        `}<span className="text-emerald-300">"style"</span>{`: `}<span className="text-amber-300">"tailwind"</span>{`,
        `}<span className="text-emerald-300">"scalabilite"</span>{`: `}<span className="text-blue-300">True</span>{`,
    }`}
        </pre>
      </div>
    </div>
  );
}

function AutomationMock() {
  const steps = [
    { icon: MousePointer2, label: "Aller sur un site" },
    { icon: Database, label: "Extraire des données" },
    { icon: ListChecks, label: "Formater les résultats" },
    { icon: Send, label: "Envoyer un rapport" },
  ];
  return (
    <div className="mt-6 rounded-xl border border-white/10 bg-[#0a0e1a]/80 p-4 relative overflow-hidden min-h-[210px]">
      <div
        className="absolute -right-6 -bottom-8 h-40 w-40 rounded-full border border-blue-500/20"
        style={{ background: "radial-gradient(circle at 40% 30%, rgba(59,130,246,0.28), transparent 62%)" }}
      />
      <div className="relative space-y-2.5">
        {steps.map((s, i) => (
          <div key={s.label} className="flex items-center gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-slate-100">
              <s.icon className="h-4 w-4 text-cyan-300" />
              {s.label}
            </div>
            {i < steps.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-blue-400/60" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailMock() {
  const folders = ["Boîte de réception", "Prioritaires", "À traiter", "Envoyés", "Brouillons", "Archives"];
  const mails = [
    { who: "Sara", sub: "Re: Mise à jour du projet", time: "10:24" },
    { who: "Client", sub: "Re: Proposition commerciale", time: "09:17" },
    { who: "Équipe", sub: "Réunion de demain", time: "Hier" },
  ];
  return (
    <div className="mt-6 rounded-xl border border-white/10 bg-[#0a0e1a]/80 overflow-hidden flex text-[11px] min-h-[210px]">
      <div className="w-32 shrink-0 border-r border-white/5 p-2 space-y-1">
        {folders.map((f, i) => (
          <div
            key={f}
            className={`flex items-center justify-between rounded px-1.5 py-1 ${
              i === 0 ? "bg-blue-500/15 text-blue-200" : "text-slate-500"
            }`}
          >
            <span className="truncate">{f}</span>
            {i === 0 && <span className="rounded bg-blue-500/30 px-1 text-[9px] text-blue-100">12</span>}
          </div>
        ))}
      </div>
      <div className="flex-1 min-w-0 p-2">
        <div className="mb-2 font-medium text-slate-300">Nouveau message</div>
        <div className="space-y-1.5">
          {mails.map((m) => (
            <div key={m.who} className="rounded-lg border border-white/8 bg-white/[0.03] p-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-white">{m.who}</span>
                <span className="text-slate-500">{m.time}</span>
              </div>
              <div className="truncate text-slate-400">{m.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---- trading desk preview (premium) -------------------------------- */
function TradingMock() {
  const bars = [40, 55, 47, 66, 58, 74, 63];
  const signals = [
    { s: "ARB", d: "ACHAT", c: 75, up: true },
    { s: "ETH", d: "ACHAT", c: 70, up: true },
    { s: "XAU", d: "VENTE", c: 64, up: false },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#070b16]/90 overflow-hidden shadow-[0_30px_80px_-30px_rgba(56,189,248,0.4)]">
      <div className="flex items-center justify-between border-b border-white/8 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 text-[10px] font-black text-white">SC</span>
          <div>
            <div className="text-[12px] font-bold text-white leading-none">Sara Connor · Desk</div>
            <div className="mt-1 text-[9px] uppercase tracking-widest text-slate-500">Institutionnel · temps réel</div>
          </div>
        </div>
        <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-cyan-300">Papier</span>
      </div>

      <div className="grid grid-cols-3 gap-px bg-white/5">
        {[
          { l: "Équité", v: "50 117 €", c: "text-white" },
          { l: "P&L jour", v: "+16,55 €", c: "text-emerald-300" },
          { l: "Drawdown", v: "0,21 %", c: "text-slate-200" },
        ].map((m) => (
          <div key={m.l} className="bg-[#070b16] px-3 py-2.5">
            <div className="text-[9px] uppercase tracking-widest text-slate-500">{m.l}</div>
            <div className={`mt-1 font-mono text-sm font-bold tabular-nums ${m.c}`}>{m.v}</div>
          </div>
        ))}
      </div>

      <div className="px-4 pt-3">
        <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-slate-500">
          <span>Performance hebdo</span>
          <span className="text-emerald-400">+2,3 %</span>
        </div>
        <div className="mt-2 flex h-16 items-end gap-1.5">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-cyan-500/25 to-cyan-400/80" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>

      <div className="p-3 space-y-1.5">
        {signals.map((sg) => (
          <div key={sg.s} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2">
            <div className="flex items-center gap-2">
              <span className={`h-1.5 w-1.5 rounded-full ${sg.up ? "bg-emerald-400" : "bg-rose-400"}`} />
              <span className="font-mono text-xs font-bold text-white">{sg.s}</span>
              <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${sg.up ? "bg-emerald-400/15 text-emerald-300" : "bg-rose-400/15 text-rose-300"}`}>{sg.d}</span>
            </div>
            <span className="font-mono text-[11px] text-slate-400">conf. {sg.c}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- page ----------------------------------------------------------- */
export default function Landing() {
  const nav = [
    { label: "Accueil", href: "#top" },
    { label: "Fonctionnalités", href: "#features" },
    { label: "Trading", href: "#trading" },
    { label: "Tarifs", href: "#stats" },
    { label: "À propos", href: "#understand" },
    { label: "Blog", href: "#features" },
  ];

  const heroImgRef = useRef(null);
  const glowRef = useRef(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const rafRef = useRef(0);
  const onHeroMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    const lx = e.clientX - r.left;
    const ly = e.clientY - r.top;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (heroImgRef.current)
        heroImgRef.current.style.transform = `scale(1.08) translate(${nx * -18}px, ${ny * -14}px)`;
      if (glowRef.current) {
        glowRef.current.style.left = `${lx}px`;
        glowRef.current.style.top = `${ly}px`;
        glowRef.current.style.opacity = "1";
      }
    });
  };
  const onHeroLeave = () => {
    if (heroImgRef.current) heroImgRef.current.style.transform = "scale(1.04) translate(0,0)";
    if (glowRef.current) glowRef.current.style.opacity = "0";
  };

  return (
    <div id="top" className="relative min-h-screen overflow-x-hidden text-white" style={{ background: BG }} data-testid="landing-page">
      {/* ambient glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 right-0 h-[600px] w-[600px] rounded-full" style={{ background: "radial-gradient(circle, rgba(56,189,248,0.16), transparent 60%)", filter: "blur(30px)" }} />
        <div className="absolute top-[40%] -left-40 h-[560px] w-[560px] rounded-full" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.14), transparent 60%)", filter: "blur(30px)" }} />
      </div>

      <style>{`
        @keyframes sara-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .sara-marquee-track { animation: sara-marquee 30s linear infinite; }
        .sara-marquee-track:hover { animation-play-state: paused; }
      `}</style>

      {/* ---------- NAV ---------- */}
      <header className="sticky top-0 z-50 border-b border-white/8 bg-[#05060e]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-7 w-7 items-center justify-center rounded-full border border-cyan-400/50">
              <span className="h-3 w-3 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500" />
            </span>
            <span className="text-lg font-black tracking-[0.28em] text-white">SARA</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            {nav.map((n) => (
              <a key={n.label} href={n.href} className="text-sm text-slate-300 hover:text-white transition-colors">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <Link data-testid="nav-login" to="/auth" className="rounded-full px-4 py-2 text-sm font-medium text-slate-200 border border-white/12 bg-white/[0.03] hover:bg-white/[0.08] transition-all">
              Se connecter
            </Link>
            <PrimaryBtn data-testid="nav-cta" className="px-5 py-2.5 hidden sm:inline-flex">
              Commencer gratuitement
            </PrimaryBtn>
          </div>
        </div>
      </header>

      {/* ---------- HERO ---------- */}
      <section className="relative" onMouseMove={onHeroMove} onMouseLeave={onHeroLeave}>
        {/* right portrait (desktop, full-bleed, fades into bg) */}
        <div className="hidden lg:block absolute right-0 top-0 h-full w-[54%] overflow-hidden">
          <img
            ref={heroImgRef}
            src="/hero_portrait.jpg"
            alt="Sara"
            className="h-full w-full object-cover object-[center_20%]"
            style={{ transform: "scale(1.04)", transition: "transform 0.25s ease-out", willChange: "transform" }}
          />
          <div
            ref={glowRef}
            className="pointer-events-none absolute h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0"
            style={{ left: 0, top: 0, background: "radial-gradient(circle, rgba(56,189,248,0.35), transparent 60%)", filter: "blur(20px)", transition: "opacity 0.3s ease" }}
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${BG} 0%, rgba(5,6,14,0.55) 22%, transparent 55%)` }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(0deg, ${BG} 2%, transparent 32%)` }} />
          {/* online chip + quote */}
          <div className="absolute right-[6%] top-28 text-right">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-white">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_#34d399]" />
              Sara
            </div>
            <div className="text-xs text-emerald-400 mt-0.5">En ligne</div>
            <p className="mt-4 max-w-[190px] ml-auto text-sm italic leading-relaxed text-slate-300">
              «&nbsp;Je suis Sara, prête à vous aider. Que souhaitez-vous aujourd'hui&nbsp;?&nbsp;»
            </p>
          </div>
        </div>

        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 sm:px-6 py-16 sm:py-24 lg:grid-cols-2">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-3 py-1 text-[11px] font-medium tracking-[0.15em] uppercase text-cyan-300">
              <Zap className="h-3 w-3" /> IA · Assistante avancée
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-white">
              Sara, votre alliée<br />
              <GradientText>intelligente</GradientText> au quotidien
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400">
              Sara est une intelligence artificielle de nouvelle génération, conçue pour vous faire
              gagner du temps, automatiser vos tâches et booster votre productivité. Plus qu'un simple
              assistant, c'est une partenaire qui comprend vos besoins et agit avec précision.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <PrimaryBtn data-testid="hero-cta-primary">
                Commencer gratuitement
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </PrimaryBtn>
              <DemoBtn data-testid="hero-cta-secondary" onClick={() => setDemoOpen(true)} />
            </div>

            {/* mobile portrait */}
            <div className="mt-10 lg:hidden overflow-hidden rounded-2xl border border-white/10">
              <img src="/hero_portrait.jpg" alt="Sara" className="w-full object-cover" />
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <HeroBullet icon={Zap} title="Ultra rapide" sub="Réponses en quelques secondes" />
              <HeroBullet icon={ShieldCheck} title="Sécurisé" sub="Vos données sont protégées" />
              <HeroBullet icon={Clock} title="Toujours disponible" sub="24/24 et 7/7" />
            </div>
          </Reveal>
          <div className="hidden lg:block" />
        </div>
      </section>

      {/* ---------- LOGOS STRIP ---------- */}
      <section className="relative border-y border-white/8 py-8">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center text-[11px] font-semibold tracking-[0.35em] uppercase text-slate-500 mb-6">
            Ils font confiance à Sara
          </div>
          <div className="relative overflow-hidden" style={{ maskImage: "linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)", WebkitMaskImage: "linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)" }}>
            <div className="sara-marquee-track flex w-max items-center gap-12">
              {[...LOGOS, ...LOGOS].map((l, i) => (
                <span key={i} className="flex items-center gap-2 text-lg font-black tracking-widest text-slate-600 hover:text-slate-300 transition-colors whitespace-nowrap">
                  <span className="h-2.5 w-2.5 rotate-45 rounded-sm bg-gradient-to-br from-cyan-400/60 to-violet-500/60" />
                  {l}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FEATURES ---------- */}
      <section id="features" className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal className="text-center mb-12 sm:mb-16">
            <div className="text-[11px] font-semibold tracking-[0.35em] uppercase text-slate-500">Ses super-pouvoirs</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-white">
              Des fonctionnalités <GradientText>puissantes</GradientText>
            </h2>
            <p className="mt-3 text-slate-400">Trois piliers pour une productivité sans limite.</p>
          </Reveal>

          <div className="grid gap-5 md:grid-cols-3" data-testid="features-grid">
            <Reveal className="h-full">
            <GlassCard className="h-full">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-blue-500/20 to-violet-500/20">
                <Code2 className="h-5 w-5 text-cyan-300" />
              </div>
              <h3 className="mt-5 text-xl font-bold text-white">Génération de code sur mesure</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Sara crée du code propre, optimisé et adapté à vos besoins. Du simple script aux applications complexes.
              </p>
              <a href="#" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300">
                Découvrir la fonctionnalité <ArrowRight className="h-3.5 w-3.5" />
              </a>
              <CodeMock />
            </GlassCard>
            </Reveal>

            <Reveal className="h-full" delay={120}>
            <GlassCard className="h-full">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-blue-500/20 to-violet-500/20">
                <Globe className="h-5 w-5 text-cyan-300" />
              </div>
              <h3 className="mt-5 text-xl font-bold text-white">Automatisation web</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Elle automatise vos tâches en ligne : remplissage de formulaires, extraction de données, surveillance, et bien plus.
              </p>
              <a href="#" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300">
                Découvrir la fonctionnalité <ArrowRight className="h-3.5 w-3.5" />
              </a>
              <AutomationMock />
            </GlassCard>
            </Reveal>

            <Reveal className="h-full" delay={240}>
            <GlassCard className="h-full">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-blue-500/20 to-violet-500/20">
                <Mail className="h-5 w-5 text-cyan-300" />
              </div>
              <h3 className="mt-5 text-xl font-bold text-white">Gestion des emails</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Sara lit, trie, rédige et répond à vos emails depuis une seule interface. Vous restez focus sur l'essentiel.
              </p>
              <a href="#" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300">
                Découvrir la fonctionnalité <ArrowRight className="h-3.5 w-3.5" />
              </a>
              <EmailMock />
            </GlassCard>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- TRADING (premium) ---------- */}
      <section id="trading" className="relative border-t border-white/8 py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-cyan-300">
                <Sparkles className="h-3 w-3" /> Module premium
              </div>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.08] tracking-tight text-white">
                Trading avec <GradientText>Sara Connor</GradientText>
                <br />
                votre IA premium
              </h2>
              <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400">
                Un desk de trading de niveau institutionnel, piloté 24/7 par Sara en mode papier.
                Elle analyse les marchés en continu, propose des signaux argumentés et applique des
                règles de gestion du risque strictes — pendant que vous gardez le contrôle total.
              </p>

              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <HeroBullet icon={Bot} title="Bot 24/7" sub="Analyse et exécution papier en continu" />
                <HeroBullet icon={ShieldCheck} title="Risque maîtrisé" sub="Stops, exposition et perte max encadrés" />
                <HeroBullet icon={Brain} title="Comité d'investissement IA" sub="Chaque entrée validée par un 2ᵉ avis IA" />
                <HeroBullet icon={Activity} title="Signaux temps réel" sub="Prix réels, confluence multi-marchés" />
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <PrimaryBtn to="/auth" data-testid="trading-cta">
                  Accéder au desk
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </PrimaryBtn>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  <Lock className="h-3.5 w-3.5" /> Trading papier — aucun capital réel engagé
                </span>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <TradingMock />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- UNDERSTAND ---------- */}
      <section id="understand" className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-[11px] font-semibold tracking-[0.35em] uppercase text-slate-500 mb-8">Plus qu'une IA</div>
          <div className="grid items-center gap-10 lg:grid-cols-[0.85fr_1fr_1fr]">
            <Reveal className="overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_-30px_rgba(139,92,246,0.4)]">
              <img src="/understand_portrait.jpg" alt="Sara" className="h-full w-full object-cover" />
            </Reveal>

            <Reveal delay={120}>
              <h2 className="text-3xl sm:text-4xl font-black leading-tight tracking-tight text-white">
                Une intelligence qui vous comprend
              </h2>
              <p className="mt-4 text-slate-400 leading-relaxed">
                Sara apprend de vos habitudes, s'adapte à votre style de travail et vous fournit des
                réponses toujours plus pertinentes. Plus vous l'utilisez, plus elle devient efficace.
              </p>
              <PrimaryBtn className="mt-7">
                En savoir plus <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </PrimaryBtn>
            </Reveal>

            <Reveal className="space-y-7" delay={240}>
              <UnderstandItem icon={Brain} title="Apprentissage continu">
                Elle s'adapte à vos préférences et à vos objectifs.
              </UnderstandItem>
              <UnderstandItem icon={SlidersHorizontal} title="Personnalisation avancée">
                Une expérience sur mesure, selon vos besoins.
              </UnderstandItem>
              <UnderstandItem icon={Smartphone} title="Multiplateforme">
                Disponible sur web, mobile et desktop.
              </UnderstandItem>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- TESTIMONIALS ---------- */}
      <section id="testimonials" className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal className="text-center mb-12 sm:mb-16">
            <div className="text-[11px] font-semibold tracking-[0.35em] uppercase text-slate-500">Témoignages</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-white">
              Ils ont adopté <GradientText>Sara</GradientText>
            </h2>
            <p className="mt-3 text-slate-400">Des milliers de professionnels lui confient leur quotidien.</p>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-3" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} className="h-full" delay={i * 120}>
                <GlassCard className="h-full">
                  <div className="flex gap-0.5 text-amber-400">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <p className="mt-4 text-slate-300 leading-relaxed">«&nbsp;{t.quote}&nbsp;»</p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-sm font-bold text-white">
                      {t.initials}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{t.name}</div>
                      <div className="text-xs text-slate-400">{t.role}</div>
                    </div>
                  </div>
                </GlassCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- STATS ---------- */}
      <section id="stats" className="relative border-t border-white/8 py-14">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-8 px-4 sm:px-6 md:grid-cols-4" data-testid="stats-grid">
          {[
            { v: "+250 000", l: "Utilisateurs satisfaits" },
            { v: "99,9%", l: "Taux de disponibilité" },
            { v: "3x", l: "Plus rapide qu'un assistant classique" },
            { v: "24/7", l: "À vos côtés, toujours" },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 100}>
              <div className="text-3xl sm:text-4xl font-black">
                <GradientText>{s.v}</GradientText>
              </div>
              <div className="mt-1.5 text-xs sm:text-sm text-slate-400">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="relative py-20 sm:py-28">
        <Reveal className="mx-auto max-w-3xl px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight">
            Prêt à travailler avec <GradientText>Sara</GradientText> ?
          </h2>
          <p className="mt-4 text-lg text-slate-400">
            Rejoignez les milliers d'utilisateurs qui gagnent du temps chaque jour.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryBtn data-testid="footer-cta-primary" className="px-7 py-4">
              Commencer gratuitement <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </PrimaryBtn>
            <DemoBtn data-testid="footer-cta-demo" onClick={() => setDemoOpen(true)} />
          </div>
        </Reveal>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="border-t border-white/8 py-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-cyan-400/50">
              <span className="h-2.5 w-2.5 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500" />
            </span>
            <span className="text-sm font-black tracking-[0.28em] text-white">SARA</span>
          </div>
          <div className="flex gap-6 text-xs text-slate-500">
            <a href="#" className="hover:text-white">Confidentialité</a>
            <a href="#" className="hover:text-white">CGU</a>
            <a href="#" className="hover:text-white">Contact</a>
          </div>
          <div className="text-xs text-slate-600">© 2026 Sara · Tous droits réservés</div>
        </div>
      </footer>

      <DemoVideoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
